import { Colors, ConfirmDialog, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { getPillarDeleteImpactMessage } from './pillarImpactMessage';

import type { Pillar } from '@/types/pillars';

import { useDeletePillar } from '@/api/pillars';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';

interface DeletePillarDialogProps {
    isOpen: boolean;
    pillar: Pillar | null;
    onCancel: () => void;
    onSuccess: () => void;
}

export default function DeletePillarDialog({
    isOpen,
    pillar,
    onCancel,
    onSuccess,
}: DeletePillarDialogProps) {
    const { t } = useTranslation();
    const { handleError } = useError();
    const deleteMutation = useDeletePillar();

    const handleDelete = async () => {
        if (!pillar) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(pillar.id);
            showSuccessToast({ message: t('pillars.deleteSuccess') });
            onSuccess();
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        // Mounted inside the pillar card, whose surface reacts to clicks: React bubbles the
        // portal's clicks up the component tree, so they stop here.
        <div onClick={(event) => event.stopPropagation()}>
            <ConfirmDialog
                isOpen={isOpen}
                title={t('pillars.deleteTitle', { name: getLocalizedText(pillar?.name) })}
                message={
                    <div className={'flex flex-col gap-4'}>
                        <Text>{t('pillars.deleteMessage')}</Text>

                        <Text color={Colors.BASE_600}>
                            {getPillarDeleteImpactMessage(t, pillar?.linkedObjectivesCount)}
                        </Text>
                    </div>
                }
                confirmLabel={t('pillars.delete')}
                cancelLabel={t('pillars.cancel')}
                onConfirm={handleDelete}
                onCancel={onCancel}
                isLoading={deleteMutation.isPending}
                isDestructive
                dataTestId={'delete-pillar-dialog'}
            />
        </div>
    );
}
